/**
 * V4P PDF Generator - UPDATED
 * 
 * Renders each page separately to avoid blank pages.
 * Uses pixel dimensions for html2canvas compatibility.
 * 
 * Updates:
 * - Addresses on separate lines (one field per line)
 * - Pet details on separate lines
 * - Pet health screening vertical layout
 * - Changed "Waste" to "Units Remaining" column
 * - Fixed sign-off section to use responsibleStaff data
 * - Vet RCVS number
 * - Customer address broken into separate fields
 * - Product badges (POM-V, CD, Cold Chain) in order lines
 * - Page 3: Full verification checklist (11 items)
 * - Page 3: Checklist summary
 * - Page 3: Sign-off section
 * - Page 3: Unit tracking section for repeats
 * - Footer reference
 */

(function() {
  'use strict';

  const CONFIG = window.V4P_SUBMISSION_CONFIG || {
    pdfWorkerUrl: 'https://v4p-order-pdf.automation-b3f.workers.dev/',
    autoDownloadPdf: false
  };


  const ROUTE_LABELS = {
    'DTP': 'Direct to Pharmacy',
    'VTP': 'V4P to Pharmacy',
    'VTCP': 'V4P to Customer (Packs)',
    'VTCU': 'V4P to Customer (Units)',
    'VTCP+VTCU': 'V4P to Customer (Packs + Units)'
  };

  const ROLE_LABELS = {
    'pharmacist': 'Pharmacist',
    'pharmacy_technician': 'Pharmacy Technician',
    'dispensing_technician': 'Dispensing Technician',
    'pharmacy_assistant': 'Pharmacy Assistant',
    'counter_assistant': 'Counter Assistant',
    'dispenser': 'Dispenser',
    'veterinary_surgeon': 'Veterinary Surgeon',
    'veterinary_nurse': 'Veterinary Nurse',
    'administrator': 'Administrator',
    'other': 'Staff'
  };

  const FULFILMENT_LABELS = {
    'collection': 'Collection',
    'delivery': 'Delivery'
  };

  // ============================================
  // PDF FILENAME
  // ============================================
  
  function generatePdfFilename(data) {
    // Get customer name formatted for filename - UPPERCASE
    const customerName = `${data.customer.firstName}_${data.customer.lastName}`.trim().replace(/\s+/g, '_').toUpperCase();
    
    if (data.prescription?.filename) {
      // e.g., "RX_Customer_2026-01-11T23-01-09.png" -> "RX_RUSHIL_KOTECHA_2026-01-11T23-01-09.pdf"
      let baseName = data.prescription.filename.replace(/\.[^/.]+$/, ''); // Remove extension
      baseName = baseName.replace(/Customer/gi, customerName); // Replace Customer with actual name
      return `${baseName}.pdf`;
    }
    
    // Fallback
    const timestamp = new Date(data.order.timestamp).toISOString().split('.')[0].replace(/:/g, '-');
    return `RX_${customerName}_${timestamp}.pdf`;
  }

  // ============================================
  // POPULATE TEMPLATE
  // ============================================
  
  function populatePdfTemplate(data) {
    console.log('Populating PDF template with data:', data);
    
    // ─────────────────────────────────────────────────────────────────────
    // PAGE 1: ORDER DETAILS
    // ─────────────────────────────────────────────────────────────────────
    
    // Header
    setText('pdf-order-ref', data.order.reference);
    setText('pdf-order-date', formatDateTime(data.order.timestamp));
    setText('pdf-route-badge', data.order.route);
    setText('pdf-route-label', ROUTE_LABELS[data.order.route] || data.order.route);
    setText('pdf-order-ref-p1-orders', data.order.reference);
    setText('pdf-order-date-p1-orders', formatDateTime(data.order.timestamp));

    // Page footers - all pages
    setText('pdf-footer-ref-p1', data.order.reference);
    setText('pdf-footer-ref-p2', data.order.reference);
    setText('pdf-footer-ref-p3', data.order.reference);
    setText('pdf-footer-ref-p4', data.order.reference);
    setText('pdf-footer-ref-p1-orders', data.order.reference);
    setText('pdf-footer-ref-p4-orders', data.order.reference);


    const routeBadgeEl = document.getElementById('pdf-route-badge');
    if (routeBadgeEl) {
      // remove previous route classes
      routeBadgeEl.classList.remove(
        'v4p-pdf__badge--dtp',
        'v4p-pdf__badge--vtp',
        'v4p-pdf__badge--vtcp',
        'v4p-pdf__badge--vtcu',
        'v4p-pdf__badge--combo'
      );

      const r = String(data.order.route || '').toUpperCase();
      if (r === 'DTP') routeBadgeEl.classList.add('v4p-pdf__badge--dtp');
      else if (r === 'VTP') routeBadgeEl.classList.add('v4p-pdf__badge--vtp');
      else if (r === 'VTCP') routeBadgeEl.classList.add('v4p-pdf__badge--vtcp');
      else if (r === 'VTCU') routeBadgeEl.classList.add('v4p-pdf__badge--vtcu');
      else if (r === 'VTCP+VTCU') routeBadgeEl.classList.add('v4p-pdf__badge--combo');
    }
    
    // Fulfilment
    const fulfilmentBadge = document.getElementById('pdf-fulfilment-badge');
    if (fulfilmentBadge) {
      fulfilmentBadge.textContent = FULFILMENT_LABELS[data.order.fulfilmentType] || 'Collection';
    }
    
    // Order type badges - show both if applicable
    const hasPacks = data.orderLines.some(line => line.orderType === 'packs');
    const hasUnits = data.orderLines.some(line => line.orderType === 'units');
    
    const packsBadge = document.getElementById('pdf-packs-badge');
    const unitsBadge = document.getElementById('pdf-units-badge');
    if (packsBadge) packsBadge.style.display = hasPacks ? 'inline-block' : 'none';
    if (unitsBadge) unitsBadge.style.display = hasUnits ? 'inline-block' : 'none';
    
    // Pharmacy - separate lines
    setText('pdf-pharmacy-name', data.pharmacy.name);
    setText('pdf-pharmacy-line1', data.pharmacy.address?.line1 || '');
    setText('pdf-pharmacy-line2', data.pharmacy.address?.line2 || '');
    setText('pdf-pharmacy-city', data.pharmacy.address?.city || '');
    setText('pdf-pharmacy-postcode', data.pharmacy.address?.postcode || '');
    setText('pdf-pharmacy-contact', `Tel: ${data.pharmacy.phone || 'N/A'} • ${data.pharmacy.email || ''}`);
    
    // Vet Practice (with RCVS number) — skip for non-vet
    if (data.orderCategory !== 'non-vet') {
      setText('pdf-vet-name', data.vetPractice?.name || 'Not specified');
      setText('pdf-vet-postcode', data.vetPractice?.postcode ? `Postcode: ${data.vetPractice.postcode.toUpperCase()}` : '');
      setText('pdf-vet-rcvs', data.vetPractice?.rcvsNumber ? `RCVS: ${data.vetPractice.rcvsNumber}` : '');
      setText('pdf-rx-issued-inline', data.prescription?.dateIssued ? `Issued: ${formatDate(data.prescription.dateIssued)}` : '');
      setText('pdf-rx-expiry-inline', data.prescription?.expiryDate ? `Expires: ${formatDate(data.prescription.expiryDate)}` : '');
    }
    
    // Staff
    setText('pdf-staff-name', `${data.responsibleStaff.firstName} ${data.responsibleStaff.lastName}`.trim());
    setText('pdf-staff-role', ROLE_LABELS[data.responsibleStaff.role] || data.responsibleStaff.role);
    setText('pdf-staff-reg', data.responsibleStaff.registrationNumber ? `Registration: ${data.responsibleStaff.registrationNumber}` : '');
    
    // Customer - separate lines for address
    setText('pdf-customer-name', `${data.customer.firstName} ${data.customer.lastName}`.trim());
    setText('pdf-customer-phone', data.customer.phone || '');
    setText('pdf-customer-email', data.customer.email || '');
    
    // Separate address lines
    setText('pdf-customer-address-line1', data.customer.address?.line1 || '');
    setText('pdf-customer-address-line2', data.customer.address?.line2 || '');
    setText('pdf-customer-city', data.customer.address?.city || '');
    setText('pdf-customer-county', data.customer.address?.county || '');
    setText('pdf-customer-postcode', data.customer.address?.postcode?.toUpperCase() || '');
    setText('pdf-customer-country', data.customer.address?.country === 'GB' ? 'United Kingdom' : (data.customer.address?.country || ''));
    
    // Pet - separate lines for each detail
    setText('pdf-pet-name', data.pet.name);
    setText('pdf-pet-species', `(${data.pet.species})`);
    setText('pdf-pet-breed', data.pet.breed ? `Breed: ${data.pet.breed}` : '');
    setText('pdf-pet-age', data.pet.age ? `Age: ${data.pet.age}` : '');
    setText('pdf-pet-weight', data.pet.weightKg ? `Weight: ${data.pet.weightKg}kg` : '');
    setText('pdf-pet-gender', data.pet.gender ? `Gender: ${data.pet.gender}` : '');
    setText('pdf-pet-neutering', data.pet.neuteringStatus ? `Status: ${data.pet.neuteringStatus}` : '');
    setText('pdf-pet-microchip', data.pet.microchip ? `Microchip: ${data.pet.microchip}` : '');
    setText('pdf-pet-dob', data.pet.dob ? `DOB: ${formatDate(data.pet.dob)}` : '');
    
    // Screening - vertical layout
    setIcon('pdf-screen-allergies', data.pet.hasAllergies);
    setIcon('pdf-screen-meds', data.pet.onOtherMeds);
    setIcon('pdf-screen-vets4u', data.pet.registeredVets4u);
    setIcon('pdf-screen-info', data.pet.readProductInfo);
    if (data.pet.hasAllergies && data.pet.allergiesDetails) setText('pdf-allergies-detail', `(${data.pet.allergiesDetails})`);
    if (data.pet.onOtherMeds && data.pet.otherMedsDetails) setText('pdf-meds-detail', `(${data.pet.otherMedsDetails})`);
    
    // ─────────────────────────────────────────────────────────────────────
    // ORDER LINES - With pagination (max 3 per page)
    // ─────────────────────────────────────────────────────────────────────
    const CONT_LINES_PER_PAGE = 12; // continuation page capacity
    const orderLines = data.orderLines || [];
    const hasOverflow = orderLines.length > CONT_LINES_PER_PAGE;
    const overflowLines = orderLines.slice(CONT_LINES_PER_PAGE);

    function chunkArray(arr, size) {
      const out = [];
      for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
      return out;
    }

    // Helper to build a product row for Page 1
    function buildOrderLineRow(line) {
      const tr = document.createElement('tr');
      
      // Build product badges HTML
      let badgesHtml = '';
      if (line.controlledDrug) badgesHtml += '<span class="v4p-pdf__product-badge v4p-pdf__product-badge--cd">CD</span>';
      if (line.coldChain) badgesHtml += '<span class="v4p-pdf__product-badge v4p-pdf__product-badge--cold">❄️</span>';
      if (!badgesHtml) badgesHtml = '—';
      
      let repeatHtml = '—';
      if (line.repeatType === 'repeat') {
        repeatHtml = `<span class="v4p-pdf__repeat-badge">🔄 ${line.currentDispense || '?'}/${line.totalRepeatsAllowed || '?'}</span>`;
        if (line.intervalDays) repeatHtml += `<br><small style="color:#666;">Every ${line.intervalDays} days</small>`;
      }
      
      const typeBadgeClass = line.orderType === 'units' ? 'v4p-pdf__type-badge--units' : 'v4p-pdf__type-badge--packs';
      const typeLabel = line.orderType === 'units' ? 'Units' : 'Packs';
      const unitsRemainingDisplay = (line.waste !== null && line.waste !== undefined) ? line.waste : '—';
      
      tr.innerHTML = `
        <td><strong>${escapeHtml(line.productName)}</strong></td>
        <td>${badgesHtml}</td>
        <td>${escapeHtml(line.uom || '')}</td>
        <td style="text-align: center;">${line.quantity || 1}</td>
        <td style="text-align: center;">${line.totalUnits || '—'}</td>
        <td style="text-align: center;">${line.unitsRequested || '—'}</td>
        <td style="text-align: center;">${unitsRemainingDisplay}</td>
        <td>${repeatHtml}</td>
        <td style="text-align: right;">£${(line.pharmacyPrice || 0).toFixed(2)}</td>
        <td style="text-align: right;">£${((line.pharmacyPrice || 0) / (line.totalUnits || 1)).toFixed(2)}</td>
        <td style="text-align: right;"><strong>£${(line.customerPrice || 0).toFixed(2)}</strong></td>
        <td style="text-align: right;">£${((line.customerPrice || 0) / (line.unitsRequested || line.totalUnits || 1)).toFixed(2)}</td>
        <td><span class="v4p-pdf__type-badge ${typeBadgeClass}">${typeLabel}</span></td>
      `;
      return tr;
    }

    // Helper to build delivery row
    function buildDeliveryRow(delivery) {
      const deliveryTr = document.createElement('tr');
      deliveryTr.className = 'v4p-pdf__delivery-row';
      const deliveryType = delivery.type || 'Standard';
      const deliveryIcon = deliveryType === 'COLD_CHAIN' ? '❄️ ' : (deliveryType === 'COMBINED' ? '📦❄️ ' : '📦 ');
      const hasPharmacyDelivery = delivery.pharmacyDelivery > 0;
      const hasCustomerDelivery = delivery.customerDelivery > 0;
      
      deliveryTr.innerHTML = `
        <td><strong>${deliveryIcon}DELIVERY (${deliveryType.replace('_', ' ')})</strong></td>
        <td>—</td>
        <td>—</td>
        <td style="text-align: center;">1</td>
        <td style="text-align: center;">—</td>
        <td style="text-align: center;">—</td>
        <td style="text-align: center;">—</td>
        <td>—</td>
        <td style="text-align: right;">${hasPharmacyDelivery ? '£' + delivery.pharmacyDelivery.toFixed(2) : '—'}</td>
        <td style="text-align: right;">—</td>
        <td style="text-align: right;"><strong>${hasCustomerDelivery ? '£' + delivery.customerDelivery.toFixed(2) : '—'}</strong></td>
        <td style="text-align: right;">—</td>
        <td><span class="v4p-pdf__type-badge" style="background:#e3f2fd;color:#1565c0;">Delivery</span></td>
      `;
      return deliveryTr;
    }

    // Page 1 - First 3 products
    const linesContainer = document.getElementById('pdf-order-lines');
    if (linesContainer) {
      linesContainer.innerHTML = '';
      orderLines.slice(0, CONT_LINES_PER_PAGE).forEach(line => {
        linesContainer.appendChild(buildOrderLineRow(line));
      });
    }

    // Show "continued" note if overflow
    const continuedNote = document.getElementById('pdf-order-lines-continued-note');
    if (continuedNote) {
      continuedNote.style.display = hasOverflow ? 'block' : 'none';
    }

    // Page 1B - Overflow products + delivery
    const contPage = document.getElementById('v4p-pdf-page1-cont');
    const contLinesContainer = document.getElementById('pdf-order-lines-cont');

    if (hasOverflow && contPage && contLinesContainer) {
      // Clear any previously-cloned continuation pages (from earlier runs)
      const existingClones = contPage.parentNode.querySelectorAll('[data-p1-cont-clone="1"]');
      existingClones.forEach(n => n.remove());

      contPage.style.display = 'block';
      contPage.classList.add('v4p-pdf__page--continuation');

      const chunks = chunkArray(overflowLines, CONT_LINES_PER_PAGE);

      // Fill the template continuation page with chunk #1
      contLinesContainer.innerHTML = '';
      (chunks[0] || []).forEach(line => contLinesContainer.appendChild(buildOrderLineRow(line)));

      // Add delivery ONLY on the LAST continuation page
      const delivery = data.totals.delivery || {};
      const deliveryNeeded = (delivery.pharmacyDelivery > 0 || delivery.customerDelivery > 0);

      // Populate template continuation page header/footer (by existing IDs)
      setText('pdf-cont-order-ref', data.order.reference);
      setText('pdf-cont-order-date', formatDateTime(data.order.timestamp));
      setText('pdf-footer-ref-p1b', data.order.reference);

      let lastInserted = contPage;

      // Create extra continuation pages for chunk #2+
      for (let i = 1; i < chunks.length; i++) {
        const clone = contPage.cloneNode(true);
        clone.id = `v4p-pdf-page1-cont-${i + 1}`;
        clone.setAttribute('data-p1-cont-clone', '1');
        clone.style.display = 'block';

        // Fill via data-role selectors to avoid getElementById collisions
        const refEl = clone.querySelector('[data-role="p1-cont-order-ref"]');
        if (refEl) refEl.textContent = data.order.reference || '';
        const dateEl = clone.querySelector('[data-role="p1-cont-order-date"]');
        if (dateEl) dateEl.textContent = formatDateTime(data.order.timestamp) || '';
        const footerEl = clone.querySelector('[data-role="p1-cont-footer-ref"]');
        if (footerEl) footerEl.textContent = data.order.reference || '';

        const body = clone.querySelector('[data-role="p1-cont-lines-body"]');
        if (body) {
          body.innerHTML = '';
          chunks[i].forEach(line => body.appendChild(buildOrderLineRow(line)));
        }

        lastInserted.parentNode.insertBefore(clone, lastInserted.nextSibling);
        lastInserted = clone;
      }

      // Put delivery on the LAST continuation page (template or last clone)
      if (deliveryNeeded) {
        const lastBody = lastInserted.querySelector('[data-role="p1-cont-lines-body"]') || contLinesContainer;
        lastBody.appendChild(buildDeliveryRow(delivery));
      }

    } else if (contPage) {
      // Remove any previously-cloned continuation pages (from earlier runs)
      const existingClones = contPage.parentNode.querySelectorAll('[data-p1-cont-clone="1"]');
      existingClones.forEach(n => n.remove());

      // Hide the template continuation page AND stop it triggering pagebreak rules
      contPage.style.display = 'none';
      contPage.classList.remove('v4p-pdf__page--continuation');

      // No overflow - add delivery to Page 1
      const delivery = data.totals.delivery || {};
      if (linesContainer && (delivery.pharmacyDelivery > 0 || delivery.customerDelivery > 0)) {
        linesContainer.appendChild(buildDeliveryRow(delivery));
      }
    }


    // Totals - Split view
    const delivery = data.totals.delivery || {};
    const lineCount = data.totals.lineCount || data.orderLines.length;
    
    // Calculate items totals from order lines if not provided
    let pharmacyItemsTotal = data.totals.pharmacyItemsTotal;
    let customerItemsTotal = data.totals.customerItemsTotal;
    
    // Always recalculate from order lines to ensure accuracy
    if (pharmacyItemsTotal === undefined || pharmacyItemsTotal === null || pharmacyItemsTotal === 0) {
      pharmacyItemsTotal = data.orderLines.reduce((sum, line) => {
        return sum + (line.pharmacyPrice || line.pharmacyTotal || line.pharmacyLineTotal || 0);
      }, 0);
    }
    
    if (customerItemsTotal === undefined || customerItemsTotal === null || customerItemsTotal === 0) {
      customerItemsTotal = data.orderLines.reduce((sum, line) => {
        return sum + (line.customerPrice || line.customerTotal || line.customerLineTotal || 0);
      }, 0);
    }

    // Page 1 - Pharmacy side
    setText('pdf-line-count', lineCount);
    setText('pdf-pharmacy-items', `£${pharmacyItemsTotal.toFixed(2)}`);
    setText('pdf-pharmacy-delivery', `£${(delivery.pharmacyDelivery || 0).toFixed(2)}`);
    
    // Calculate VAT (default 20%)
    const pharmacyTotal = data.totals.pharmacyTotal || pharmacyItemsTotal;
    const pharmacyExVat = data.totals.pharmacyExVat || (pharmacyTotal / 1.2);
    const pharmacyVat = data.totals.pharmacyVat || (pharmacyTotal - pharmacyExVat);
    setText('pdf-pharmacy-ex-vat', `£${pharmacyExVat.toFixed(2)}`);
    setText('pdf-pharmacy-vat', `£${pharmacyVat.toFixed(2)}`);

    // Hide pharmacy delivery row if £0
    const pharmacyDeliveryRow = document.getElementById('pdf-pharmacy-delivery-row');
    if (pharmacyDeliveryRow) {
      pharmacyDeliveryRow.style.display = (delivery.pharmacyDelivery > 0) ? '' : 'none';
    }


    // Page 1 - Customer side
    setText('pdf-customer-line-count', lineCount);
    setText('pdf-customer-items', `£${customerItemsTotal.toFixed(2)}`);
    setText('pdf-customer-delivery', `£${(delivery.customerDelivery || data.totals.deliveryCharge || 0).toFixed(2)}`);

    const pharmacyTotalIncDelivery =
      (data.totals.pharmacyTotal ?? (pharmacyItemsTotal + (delivery.pharmacyDelivery || 0)));

    const customerTotalIncDelivery =
      (data.totals.grandTotal ?? (customerItemsTotal + (delivery.customerDelivery || data.totals.deliveryCharge || 0)));

    setText('pdf-pharmacy-total', `£${pharmacyTotalIncDelivery.toFixed(2)}`);
    setText('pdf-grand-total', `£${customerTotalIncDelivery.toFixed(2)}`);


    // Hide customer delivery row if £0
    const customerDeliveryRow = document.getElementById('pdf-customer-delivery-row');
    const customerDeliveryAmount = delivery.customerDelivery || data.totals.deliveryCharge || 0;
    if (customerDeliveryRow) {
      customerDeliveryRow.style.display = (customerDeliveryAmount > 0) ? '' : 'none';
    }
    
    // Pre-screen
    const checks = [];
    if (data.screening.rxDateChecked) checks.push('✓ Rx date');
    if (data.screening.petIdConfirmed) checks.push('✓ Pet ID');
    if (data.screening.doseMatches) checks.push('✓ Dose');
    if (data.screening.interactionsChecked) checks.push('✓ Interactions');
    setText('pdf-check-summary', checks.join(' • ') || 'Not completed');
    
    const processedBy = `${data.processedBy.firstName} ${data.processedBy.lastName}`.trim();
    const processedRole = ROLE_LABELS[data.processedBy.role] || data.processedBy.role;
    setText('pdf-processed-by', processedBy);
    setText('pdf-processed-by-role', processedRole);
    
    // Prescription
    setText('pdf-rx-issued', formatDate(data.prescription.dateIssued));
    setText('pdf-rx-expiry', formatDate(data.prescription.expiryDate));
    const prescriberInfo = data.prescription.prescriberName 
      ? `${data.prescription.prescriberName}${data.prescription.prescriberRcvs ? ` (RCVS ${data.prescription.prescriberRcvs})` : ''}`
      : 'Not specified';
    setText('pdf-rx-prescriber', prescriberInfo);

    // ─────────────────────────────────────────────────────────────────────
    // PAGE 4: CUSTOMER RECEIPT
    // ─────────────────────────────────────────────────────────────────────
    
    // Header
    setText('pdf-receipt-order-ref', data.order.reference);
    setText('pdf-receipt-order-date', formatDateTime(data.order.timestamp));
    
    const receiptFulfilmentBadge = document.getElementById('pdf-receipt-fulfilment-badge');
    if (receiptFulfilmentBadge) {
      receiptFulfilmentBadge.textContent = FULFILMENT_LABELS[data.order.fulfilmentType] || 'Collection';
    }
    
    // Pharmacy
    setText('pdf-receipt-pharmacy-name', data.pharmacy.name);
    setText('pdf-receipt-pharmacy-line1', data.pharmacy.address?.line1 || '');
    setText('pdf-receipt-pharmacy-line2', data.pharmacy.address?.line2 || '');
    setText('pdf-receipt-pharmacy-city', data.pharmacy.address?.city || '');
    setText('pdf-receipt-pharmacy-postcode', data.pharmacy.address?.postcode || '');
    setText('pdf-receipt-pharmacy-contact', `Tel: ${data.pharmacy.phone || 'N/A'} • ${data.pharmacy.email || ''}`);
    
    // Vet Practice — skip for non-vet
    if (data.orderCategory !== 'non-vet') {
      setText('pdf-receipt-vet-name', data.vetPractice?.name || 'Not specified');
      setText('pdf-receipt-vet-postcode', data.vetPractice?.postcode ? `Postcode: ${data.vetPractice.postcode.toUpperCase()}` : '');
      setText('pdf-receipt-vet-rcvs', data.vetPractice?.rcvsNumber ? `RCVS: ${data.vetPractice.rcvsNumber}` : '');
      setText('pdf-receipt-rx-issued-inline', data.prescription?.dateIssued ? `Issued: ${formatDate(data.prescription.dateIssued)}` : '');
      setText('pdf-receipt-rx-expiry-inline', data.prescription?.expiryDate ? `Expires: ${formatDate(data.prescription.expiryDate)}` : '');
    }
    
    // Staff
    setText('pdf-receipt-staff-name', `${data.responsibleStaff.firstName} ${data.responsibleStaff.lastName}`.trim());
    setText('pdf-receipt-staff-role', ROLE_LABELS[data.responsibleStaff.role] || data.responsibleStaff.role);
    setText('pdf-receipt-staff-reg', data.responsibleStaff.registrationNumber ? `Registration: ${data.responsibleStaff.registrationNumber}` : '');
    
    // Customer
    setText('pdf-receipt-customer-name', `${data.customer.firstName} ${data.customer.lastName}`.trim());
    setText('pdf-receipt-customer-phone', data.customer.phone || '');
    setText('pdf-receipt-customer-email', data.customer.email ? ` • ${data.customer.email}` : '');
    setText('pdf-receipt-customer-address-line1', data.customer.address?.line1 || '');
    setText('pdf-receipt-customer-address-line2', data.customer.address?.line2 || '');
    setText('pdf-receipt-customer-city', data.customer.address?.city || '');
    setText('pdf-receipt-customer-county', data.customer.address?.county || '');
    setText('pdf-receipt-customer-postcode', data.customer.address?.postcode?.toUpperCase() || '');
    setText('pdf-receipt-customer-country', data.customer.address?.country === 'GB' ? 'United Kingdom' : (data.customer.address?.country || ''));
    
    // Pet
    setText('pdf-receipt-pet-name', data.pet.name);
    setText('pdf-receipt-pet-species', `(${data.pet.species})`);
    setText('pdf-receipt-pet-breed', data.pet.breed ? `Breed: ${data.pet.breed}` : '');
    setText('pdf-receipt-pet-age', data.pet.age ? `Age: ${data.pet.age}` : '');
    setText('pdf-receipt-pet-weight', data.pet.weightKg ? `Weight: ${data.pet.weightKg}kg` : '');
    setText('pdf-receipt-pet-gender', data.pet.gender ? `Gender: ${data.pet.gender}` : '');
    setText('pdf-receipt-pet-neutering', data.pet.neuteringStatus ? `Status: ${data.pet.neuteringStatus}` : '');
    setText('pdf-receipt-pet-microchip', data.pet.microchip ? `Microchip: ${data.pet.microchip}` : '');
    setText('pdf-receipt-pet-dob', data.pet.dob ? `DOB: ${formatDate(data.pet.dob)}` : '');
    
    // Screening
    setIcon('pdf-receipt-screen-allergies', data.pet.hasAllergies);
    setIcon('pdf-receipt-screen-meds', data.pet.onOtherMeds);
    setIcon('pdf-receipt-screen-vets4u', data.pet.registeredVets4u);
    setIcon('pdf-receipt-screen-info', data.pet.readProductInfo);
    if (data.pet.hasAllergies && data.pet.allergiesDetails) setText('pdf-receipt-allergies-detail', `(${data.pet.allergiesDetails})`);
    if (data.pet.onOtherMeds && data.pet.otherMedsDetails) setText('pdf-receipt-meds-detail', `(${data.pet.otherMedsDetails})`);
    
    // ─────────────────────────────────────────────────────────────────────
    // PAGE 4 - Customer Receipt Order Lines with pagination
    // ─────────────────────────────────────────────────────────────────────
    
    // Helper to format "units-ish" numbers to 2dp
    function fmtUnits2dp(v) {
      if (v === null || v === undefined || v === '' || v === '—') return '—';
      const n = Number(v);
      return Number.isFinite(n) ? n.toFixed(2) : String(v);
    }

    // Helper to build a receipt row for Page 4 (matches 8 table headers in the PDF template)
    function buildReceiptLineRow(line) {
      const tr = document.createElement('tr');

      let badgesHtml = '';
      if (line.controlledDrug) badgesHtml += '<span class="v4p-pdf__product-badge v4p-pdf__product-badge--cd">CD</span>';
      if (line.coldChain) badgesHtml += '<span class="v4p-pdf__product-badge v4p-pdf__product-badge--cold">❄️</span>';
      if (!badgesHtml) badgesHtml = '—';

      let repeatHtml = '—';
      if (line.repeatType === 'repeat') {
        repeatHtml = `<span class="v4p-pdf__repeat-badge">🔄 ${line.currentDispense || '?'}/${line.totalRepeatsAllowed || '?'}</span>`;
        if (line.intervalDays) repeatHtml += `<br><small style="color:#666;">Every ${line.intervalDays} days</small>`;
      }

      const typeBadgeClass = line.orderType === 'units' ? 'v4p-pdf__type-badge--units' : 'v4p-pdf__type-badge--packs';
      const typeLabel = line.orderType === 'units' ? 'Units' : 'Packs';

      const unitsReq = (line.unitsRequested ?? line.totalUnits ?? null);
      const unitsReqNum = Number(unitsReq);
      const safeUnitsReq = (Number.isFinite(unitsReqNum) && unitsReqNum > 0) ? unitsReqNum : 1;
      const custUnit = (line.customerPrice || 0) / safeUnitsReq;

      tr.innerHTML = `
        <td><strong>${escapeHtml(line.productName)}</strong></td>
        <td>${badgesHtml}</td>
        <td>${escapeHtml(line.uom || '')}</td>
        <td style="text-align: center;">${unitsReq ?? '—'}</td>
        <td>${repeatHtml}</td>
        <td style="text-align: right;"><strong>£${(line.customerPrice || 0).toFixed(2)}</strong></td>
        <td style="text-align: right;">£${custUnit.toFixed(2)}</td>
        <td><span class="v4p-pdf__type-badge ${typeBadgeClass}">${typeLabel}</span></td>
      `;
      return tr;
    }

    function buildReceiptDeliveryRow(delivery) {
      const tr = document.createElement('tr');
      tr.className = 'v4p-pdf__delivery-row';

      const deliveryType = delivery.type || 'Standard';
      const deliveryIcon = deliveryType === 'COLD_CHAIN' ? '❄️ ' : (deliveryType === 'COMBINED' ? '📦❄️ ' : '📦 ');
      const amount = (delivery.customerDelivery || data.totals.deliveryCharge || 0);
      const show = amount > 0;

      tr.innerHTML = `
        <td><strong>${deliveryIcon}DELIVERY (${deliveryType.replace('_', ' ')})</strong></td>
        <td>—</td>
        <td>—</td>
        <td style="text-align: center;">1</td>
        <td style="text-align: center;">—</td>
        <td>—</td>
        <td style="text-align: right;"><strong>${show ? '£' + amount.toFixed(2) : '—'}</strong></td>
        <td style="text-align: right;">—</td>
        <td><span class="v4p-pdf__type-badge" style="background:#e3f2fd;color:#1565c0;">Delivery</span></td>
      `;
      return tr;
    }

    // Page 4 continuation paging: reuse the same overflowLines computed for Page 1
    // (Receipt items should mirror Page 1 item pagination)
    // Note: we chunk overflowLines again so Page 4 can have multiple continuation pages too.
    const receiptOverflowChunks = chunkArray(overflowLines, CONT_LINES_PER_PAGE);

    // Page 4 - First 3 products
    const receiptLinesContainer = document.getElementById('pdf-receipt-order-lines');
    if (receiptLinesContainer) {
      receiptLinesContainer.innerHTML = '';
      orderLines.slice(0, CONT_LINES_PER_PAGE).forEach(line => {
        receiptLinesContainer.appendChild(buildReceiptLineRow(line));
      });
    }

    // Show "continued" note if overflow
    const receiptContinuedNote = document.getElementById('pdf-receipt-lines-continued-note');
    if (receiptContinuedNote) {
      receiptContinuedNote.style.display = hasOverflow ? 'block' : 'none';
    }

    // Page 4B - Overflow products
    const receiptContPage = document.getElementById('v4p-pdf-page4-cont');
    const receiptContLinesContainer = document.getElementById('pdf-receipt-order-lines-cont');

    // ALWAYS clear previously-cloned receipt continuation pages (prevents blank pages on subsequent runs)
    if (receiptContPage && receiptContPage.parentNode) {
      const existingReceiptClones = receiptContPage.parentNode.querySelectorAll('[data-p4-cont-clone="1"]');
      existingReceiptClones.forEach(n => n.remove());
    }

    if (hasOverflow && receiptContPage && receiptContLinesContainer) {
      receiptContPage.style.display = 'block';
        receiptContPage.classList.add('v4p-pdf__page--continuation');

        // Fill the template receipt continuation page with chunk #1
        receiptContLinesContainer.innerHTML = '';
        receiptOverflowChunks[0].forEach(line => receiptContLinesContainer.appendChild(buildReceiptLineRow(line)));

        // Populate template continuation page header/footer (by existing IDs)
        setText('pdf-receipt-cont-order-ref', data.order.reference);
        setText('pdf-receipt-cont-order-date', formatDateTime(data.order.timestamp));
        setText('pdf-footer-ref-p4b', data.order.reference);

        let lastReceiptInserted = receiptContPage;

        // Create extra receipt continuation pages for chunk #2+
        for (let i = 1; i < receiptOverflowChunks.length; i++) {
          const clone = receiptContPage.cloneNode(true);
          clone.id = `v4p-pdf-page4-cont-${i + 1}`;
          clone.setAttribute('data-p4-cont-clone', '1');
          clone.style.display = 'block';

          const refEl = clone.querySelector('[data-role="p4-cont-order-ref"]');
          if (refEl) refEl.textContent = data.order.reference || '';
          const dateEl = clone.querySelector('[data-role="p4-cont-order-date"]');
          if (dateEl) dateEl.textContent = formatDateTime(data.order.timestamp) || '';
          const footerEl = clone.querySelector('[data-role="p4-cont-footer-ref"]');
          if (footerEl) footerEl.textContent = data.order.reference || '';

          const body = clone.querySelector('[data-role="p4-cont-lines-body"]');
          if (body) {
            body.innerHTML = '';
            receiptOverflowChunks[i].forEach(line => body.appendChild(buildReceiptLineRow(line)));
          }

          lastReceiptInserted.parentNode.insertBefore(clone, lastReceiptInserted.nextSibling);
          lastReceiptInserted = clone;
        }

              // Add delivery ONLY on the LAST receipt continuation page
      const d = data.totals.delivery || {};
      const amount = (d.customerDelivery || data.totals.deliveryCharge || 0);
      if (amount > 0) {
        const lastBody =
          lastReceiptInserted.querySelector('[data-role="p4-cont-lines-body"]') || receiptContLinesContainer;
        lastBody.appendChild(buildReceiptDeliveryRow(d));
      }

      } else if (receiptContPage) {
    // Remove any previously-cloned receipt continuation pages (from earlier runs)
    const existingReceiptClones = receiptContPage.parentNode.querySelectorAll('[data-p4-cont-clone="1"]');
    existingReceiptClones.forEach(n => n.remove());

    // Hide template continuation page AND stop it triggering pagebreak rules
    receiptContPage.style.display = 'none';
    receiptContPage.classList.remove('v4p-pdf__page--continuation');
  }

    function moveTailToLastPage({ tailId, mainPageId, contSelector }) {
      const tail = document.getElementById(tailId);
      if (!tail) return;

      const pages = [
        document.getElementById(mainPageId),
        ...Array.from(document.querySelectorAll(contSelector))
      ].filter(p => p && p.style.display !== 'none');

      const lastPage = pages[pages.length - 1];
      if (!lastPage) return;

      const footer = lastPage.querySelector('.v4p-pdf__page-footer');
      if (footer) lastPage.insertBefore(tail, footer);
      else lastPage.appendChild(tail);
    }

    moveTailToLastPage({
      tailId: 'pdf-p1-tail',
      mainPageId: 'v4p-pdf-page1-orders',
      contSelector: '#v4p-pdf-page1-cont, [data-p1-cont-clone="1"]'
    });

    moveTailToLastPage({
      tailId: 'pdf-p4-tail',
      mainPageId: 'v4p-pdf-page4-orders',
      contSelector: '#v4p-pdf-page4-cont, [data-p4-cont-clone="1"]'
    });

    // Page 4 - Receipt Totals (recalculate to match Page 1)
    const receiptLineCount = data.totals.lineCount || data.orderLines.length;
    const receiptItemsTotal = customerItemsTotal; // Use same calculation as Page 1
    const receiptDelivery = delivery.customerDelivery || data.totals.deliveryCharge || 0;
    const receiptGrandTotal =
      (data.totals.grandTotal ?? (receiptItemsTotal + receiptDelivery));
        
    setText('pdf-receipt-line-count', receiptLineCount);
    setText('pdf-receipt-items-total', `£${receiptItemsTotal.toFixed(2)}`);
    setText('pdf-receipt-delivery-charge', `£${receiptDelivery.toFixed(2)}`);
    setText('pdf-receipt-grand-total', `£${receiptGrandTotal.toFixed(2)}`);
    
    // Hide receipt delivery row if £0
    const receiptDeliveryRow = document.getElementById('pdf-receipt-delivery-row');
    if (receiptDeliveryRow) {
      receiptDeliveryRow.style.display = (receiptDelivery > 0) ? '' : 'none';
    }

    // Safety section - pharmacy name
    setText('pdf-safety-pharmacy-name', data.pharmacy.name || 'your pharmacy');
    
    // ─────────────────────────────────────────────────────────────────────
    // PAGE 2: PRESCRIPTION IMAGE
    // ─────────────────────────────────────────────────────────────────────
    
    // Prescription image — skip entire Rx page population for non-vet
    if (data.orderCategory !== 'non-vet') {
    const rxImage = document.getElementById('pdf-rx-image');
    if (rxImage && data.prescription.imageBase64) {
      rxImage.src = data.prescription.imageBase64;
    }
    
    // Stamps
    setText('pdf-stamp-ref', data.order.reference);
    setText('pdf-stamp-date', formatDate(data.order.timestamp));
    setText('pdf-stamp-expiry', formatDate(data.prescription.expiryDate));
    
    // Issue date on expiry stamp
    setText('pdf-stamp-issued', data.prescription?.dateIssued ? `Issued: ${formatDate(data.prescription.dateIssued)}` : 'Issued: N/A');

    // Responsible pharmacist stamp
    setText('pdf-stamp-staff-name', `${data.responsibleStaff?.firstName || ''} ${data.responsibleStaff?.lastName || ''}`.trim() || '—');
    setText('pdf-stamp-staff-role', ROLE_LABELS[data.responsibleStaff?.role] || data.responsibleStaff?.role || '—');
    setText('pdf-stamp-staff-reg',
      data.responsibleStaff?.registrationNumber
        ? `Reg: ${data.responsibleStaff.registrationNumber}`
        : 'Reg: —'
    );

    // Customer stamp
    // Customer stamp - Full details
    setText('pdf-stamp-customer-name', `${data.customer?.firstName || ''} ${data.customer?.lastName || ''}`.trim() || '—');

    const customerAddressParts = [];
    if (data.customer?.address?.line1) customerAddressParts.push(data.customer.address.line1);
    if (data.customer?.address?.line2) customerAddressParts.push(data.customer.address.line2);
    if (data.customer?.address?.city) customerAddressParts.push(data.customer.address.city);
    if (data.customer?.address?.postcode) customerAddressParts.push(data.customer.address.postcode.toUpperCase());
    setText('pdf-stamp-customer-address', customerAddressParts.join(', ') || '—');

    const customerContactParts = [];
    if (data.customer?.phone) customerContactParts.push(data.customer.phone);
    if (data.customer?.email) customerContactParts.push(data.customer.email);
    setText('pdf-stamp-customer-postcode', customerContactParts.join(' • ') || '—');

    // Patient + screening stamp
    setText('pdf-stamp-pet-name', data.pet?.name ? `${data.pet.name} (${data.pet.species || ''})`.trim() : '—');

    const petSummaryParts = [];
    if (data.pet?.breed) petSummaryParts.push(data.pet.breed);
    if (data.pet?.age) petSummaryParts.push(`${data.pet.age} years`);
    if (data.pet?.weightKg) petSummaryParts.push(`${data.pet.weightKg}kg`);
    if (data.pet?.gender) petSummaryParts.push(data.pet.gender);
    if (data.pet?.neuteringStatus) petSummaryParts.push(data.pet.neuteringStatus);
    setText('pdf-stamp-pet-details', petSummaryParts.join(' • ') || '—');

    // Screening summary for stamp
    const screeningParts = [];
    if (data.pet?.hasAllergies) screeningParts.push(`Allergies: ${data.pet.allergiesDetails || 'Yes'}`);
    if (data.pet?.onOtherMeds) screeningParts.push(`Other meds: ${data.pet.otherMedsDetails || 'Yes'}`);
    if (data.pet?.registeredVets4u) screeningParts.push('Vets4u: ✓');
    if (data.pet?.readProductInfo) screeningParts.push('Info read: ✓');
    setText('pdf-stamp-pet-microchip', screeningParts.length > 0 ? screeningParts.join(' | ') : (data.pet?.microchip ? `MC: ${data.pet.microchip}` : '—'));

    // Screening summary
    const screeningBits = [];
    screeningBits.push((data.pet?.hasAllergies ? 'A✓' : 'A✗'));
    screeningBits.push((data.pet?.onOtherMeds ? 'M✓' : 'M✗'));
    screeningBits.push((data.pet?.registeredVets4u ? 'V✓' : 'V✗'));
    screeningBits.push((data.pet?.readProductInfo ? 'I✓' : 'I✗'));

    let screeningLine = `Screen: ${screeningBits.join(' ')}`;
    if (data.pet?.hasAllergies && data.pet?.allergiesDetails) screeningLine += ` • Allergies: ${data.pet.allergiesDetails}`;
    if (data.pet?.onOtherMeds && data.pet?.otherMedsDetails) screeningLine += ` • Meds: ${data.pet.otherMedsDetails}`;

    setText('pdf-stamp-screening-summary', screeningLine);

    // Expiry warning
    if (data.prescription.expiryDate) {
      const expiry = new Date(data.prescription.expiryDate);
      const today = new Date();
      const daysLeft = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
      if (daysLeft <= 0) {
        setText('pdf-stamp-expiry-note', '⚠️ EXPIRED');
      } else if (daysLeft <= 14) {
        setText('pdf-stamp-expiry-note', `⚠️ ${daysLeft} days left`);
      } else {
        setText('pdf-stamp-expiry-note', `${daysLeft} days remaining`);
      }
    }
    
    setText('pdf-stamp-pharmacy', data.pharmacy.name);

    const pharmacyAddressParts = [];
    if (data.pharmacy?.address?.line1) pharmacyAddressParts.push(data.pharmacy.address.line1);
    if (data.pharmacy?.address?.city) pharmacyAddressParts.push(data.pharmacy.address.city);
    if (data.pharmacy?.address?.postcode) pharmacyAddressParts.push(data.pharmacy.address.postcode);
    setText('pdf-stamp-pharmacy-address', pharmacyAddressParts.join(', ') || '—');
    setText('pdf-stamp-pharmacy-email', data.pharmacy?.email || data.pharmacy?.phone || '—');

    setText('pdf-stamp-dispensed', formatDate(data.order.timestamp));
    
    // Unit tracking stamp (for repeats)
    const unitStampEl = document.getElementById('pdf-stamp-units');
    if (unitStampEl && data.repeatTracking?.repeatType === 'repeat') {
      unitStampEl.style.display = 'block';
      setText('pdf-stamp-units-today', `${data.repeatTracking.units?.todaysDispense || 0} units`);
      setText('pdf-stamp-units-allocated', `Allocated: ${data.repeatTracking.units?.totalAllocated || 0}`);
      setText('pdf-stamp-units-supplied', `Previously: ${data.repeatTracking.units?.previouslySupplied || 0}`);
      setText('pdf-stamp-units-remaining', `Remaining: ${data.repeatTracking.units?.remainingAfterToday || 0}`);
    }
    
    // Repeat stamps
    const repeatStampsContainer = document.getElementById('pdf-repeat-stamps');
    if (repeatStampsContainer) {
      repeatStampsContainer.innerHTML = '';
      const repeatLines = data.orderLines.filter(l => l.repeatType === 'repeat');

      repeatLines.forEach(line => {
        const stamp = document.createElement('div');
        stamp.className = 'v4p-pdf__stamp v4p-pdf__stamp--repeat';

        const fullName = line.productName || '';
        const uom = line.uom || '';

        stamp.innerHTML = `
          <div class="v4p-pdf__stamp-title">🔄 REPEAT</div>
          <div class="v4p-pdf__stamp-value">${line.currentDispense || '?'} of ${line.totalRepeatsAllowed || '?'}</div>
          <div class="v4p-pdf__stamp-product">
            ${escapeHtml(fullName)}<br>${escapeHtml(uom)}
          </div>
          ${line.intervalDays ? `<div class="v4p-pdf__stamp-note">Next: ${line.intervalDays} days</div>` : ''}
        `;

        repeatStampsContainer.appendChild(stamp);
      });
    }
    } // end non-vet guard for Rx page population

    // ─────────────────────────────────────────────────────────────────────
    // PAGE 3: VERIFICATION CHECKLIST
    // ─────────────────────────────────────────────────────────────────────
    
    // Footer reference
    
    // Verification checklist items (11 items) — skip for non-vet
    if (data.orderCategory !== 'non-vet' && data.verificationChecklist?.items) {
      data.verificationChecklist.items.forEach((item, index) => {
        const checkEl = document.getElementById(`pdf-check-${index + 1}`);
        if (checkEl) {
          if (item.checked) {
            checkEl.textContent = '☑';
            checkEl.classList.add('v4p-pdf__check-icon--checked');
            checkEl.classList.remove('v4p-pdf__check-icon--unchecked');
          } else {
            checkEl.textContent = '☐';
            checkEl.classList.add('v4p-pdf__check-icon--unchecked');
            checkEl.classList.remove('v4p-pdf__check-icon--checked');
          }
        }
      });
    }
    
    // Checklist summary
    const summary = data.verificationChecklist?.summary || {};
    setText('pdf-checks-completed', `${summary.totalChecked || 0} / ${summary.totalItems || 11}`);
    setText('pdf-required-checks-completed', `${summary.requiredChecked || 0} / ${summary.totalRequired || 10}`);
    
    const statusEl = document.getElementById('pdf-checklist-status');
    if (statusEl) {
      statusEl.textContent = summary.status || 'Incomplete';
      statusEl.classList.remove('v4p-pdf__checklist-status--complete', 'v4p-pdf__checklist-status--incomplete');
      if (summary.allRequiredComplete) {
        statusEl.classList.add('v4p-pdf__checklist-status--complete');
      } else {
        statusEl.classList.add('v4p-pdf__checklist-status--incomplete');
      }
    }
    
    // Sign-off section - FIXED: Now uses responsibleStaff data instead of separate signOff source
    const signOffIconEl = document.getElementById('pdf-signoff-icon');
    if (signOffIconEl) {
      if (data.signOff?.confirmed) {
        signOffIconEl.textContent = '☑';
        signOffIconEl.classList.add('v4p-pdf__check-icon--checked');
        signOffIconEl.classList.remove('v4p-pdf__check-icon--unchecked');
      } else {
        signOffIconEl.textContent = '☐';
        signOffIconEl.classList.add('v4p-pdf__check-icon--unchecked');
        signOffIconEl.classList.remove('v4p-pdf__check-icon--checked');
      }
    }
    
    // FIXED: Use responsibleStaff data for sign-off details (fallback to signOff data if available)
    const signOffStaffName = data.signOff?.staffName || 
      `${data.responsibleStaff?.firstName || ''} ${data.responsibleStaff?.lastName || ''}`.trim();
    const signOffStaffRole = data.signOff?.staffRole || data.responsibleStaff?.role || '';
    const signOffStaffReg = data.signOff?.staffRegistration || data.responsibleStaff?.registrationNumber || '';
    
    setText('pdf-signoff-name', signOffStaffName || '—');
    setText('pdf-signoff-role', ROLE_LABELS[signOffStaffRole] || signOffStaffRole || '—');
    setText('pdf-signoff-reg', signOffStaffReg || '—');
    setText('pdf-signoff-timestamp', data.signOff?.timestamp ? formatDateTime(data.signOff.timestamp) : '—');
    
    // Unit tracking section (for repeats)
    const unitTrackingSection = document.getElementById('pdf-unit-tracking-section');
    if (unitTrackingSection) {
      if (data.repeatTracking?.repeatType === 'repeat') {
        unitTrackingSection.style.display = 'block';
        
        const dispenses = data.repeatTracking.dispenses || {};
        const units = data.repeatTracking.units || {};
        const scheduling = data.repeatTracking.scheduling || {};
        
        // Dispense information
        setText('pdf-unit-repeats-written', dispenses.repeatsWritten ?? '—');
        setText('pdf-unit-includes-original', dispenses.includesOriginal ? 'Yes' : 'No');
        setText('pdf-unit-total-dispenses', dispenses.totalDispenses ?? '—');
        setText('pdf-unit-dispenses-taken', dispenses.previouslyTaken ?? '—');
        
        // Current dispense display
        if (dispenses.currentDispenseStart && dispenses.currentDispenseEnd) {
          if (dispenses.currentDispenseStart === dispenses.currentDispenseEnd) {
            setText('pdf-unit-current-dispense', `#${dispenses.currentDispenseEnd}`);
          } else {
            setText('pdf-unit-current-dispense', `#${dispenses.currentDispenseStart} - #${dispenses.currentDispenseEnd}`);
          }
        } else {
          setText('pdf-unit-current-dispense', '—');
        }
        
        setText('pdf-unit-dispenses-remaining', dispenses.remainingAfterToday ?? '—');
        
        // Unit allocation
        setText('pdf-unit-per-dispense', units.unitsPerDispense ?? '—');
        setText('pdf-unit-total-allocated', units.totalAllocated ?? '—');
        setText('pdf-unit-previously-supplied', units.previouslySupplied ?? '—');
        setText('pdf-unit-this-dispense', units.todaysDispense ?? '—');
        setText('pdf-unit-remaining', units.remainingAfterToday ?? '—');
        
        // Scheduling
        setText('pdf-unit-interval-days', scheduling.intervalDays ? `${scheduling.intervalDays} days` : '—');
        setText('pdf-unit-next-due', scheduling.nextDueDate ? formatDate(scheduling.nextDueDate) : 'N/A');
        
        // Final dispense warning
        const finalWarningEl = document.getElementById('pdf-final-dispense-warning');
        if (finalWarningEl) {
          finalWarningEl.style.display = scheduling.isFinalDispense ? 'block' : 'none';
        }
      } else {
        unitTrackingSection.style.display = 'none';
      }
    }

    // ─────────────────────────────────────────────────────────────────────
    // PAGE 2 & 3 HEADERS
    // ─────────────────────────────────────────────────────────────────────
    
    // Page 2 header
    setText('pdf-page2-order-ref', data.order.reference);
    setText('pdf-page2-order-date', formatDateTime(data.order.timestamp));
    
    // Page 3 header
    setText('pdf-page3-order-ref', data.order.reference);
    setText('pdf-page3-order-date', formatDateTime(data.order.timestamp));

    // ─────────────────────────────────────────────────────────────────────
    // PAGE 4: CUSTOMER RECEIPT - Order Processed By
    // ─────────────────────────────────────────────────────────────────────
    
    const receiptProcessedBy = `${data.processedBy.firstName} ${data.processedBy.lastName}`.trim();
    const receiptProcessedRole = ROLE_LABELS[data.processedBy.role] || data.processedBy.role;
    setText('pdf-receipt-processed-by', receiptProcessedBy || '—');
    setText('pdf-receipt-processed-by-role', receiptProcessedRole || '—');
    
    // Screening icons for receipt
    setIcon('pdf-receipt-screen-allergies', data.pet.hasAllergies);
    setIcon('pdf-receipt-screen-meds', data.pet.onOtherMeds);
    setIcon('pdf-receipt-screen-vets4u', data.pet.registeredVets4u);
    setIcon('pdf-receipt-screen-info', data.pet.readProductInfo);
    if (data.pet.hasAllergies && data.pet.allergiesDetails) {
      setText('pdf-receipt-allergies-detail', `(${data.pet.allergiesDetails})`);
    }
    if (data.pet.onOtherMeds && data.pet.otherMedsDetails) {
      setText('pdf-receipt-meds-detail', `(${data.pet.otherMedsDetails})`);
    }
  }  // <-- populatePdfTemplate() ENDS HERE (only ONE closing brace)

  // ============================================
  // GENERATE PDF - Single pass with page breaks
  // ============================================
  
  async function generatePdf(data) {
    if (typeof html2pdf === 'undefined') {
      throw new Error('html2pdf.js not loaded');
    }
    
    populatePdfTemplate(data);
    
    // ========================================
    // NON-VET: Remove prescription & checklist pages
    // ========================================
    if (data.orderCategory === 'non-vet') {
      console.log('[PDF] Non-vet order — removing Rx and checklist pages');

      // Title → "SALES ORDER (PHARMACY COPY)" for non-vet
      const page1Title = document.querySelector('#v4p-pdf-page1 .v4p-pdf__title');
      if (page1Title) page1Title.textContent = 'SALES ORDER (PHARMACY COPY)';
      
      // Remove Page 2 (Prescription image)
      const rxPage = document.getElementById('v4p-pdf-page2');
      if (rxPage) {
        rxPage.remove();
        console.log('[PDF] Removed page: v4p-pdf-page2 (Prescription)');
      }
      
      // Remove Page 3 (Screening checklist)
      const checklistPage = document.getElementById('v4p-pdf-page3');
      if (checklistPage) {
        checklistPage.remove();
        console.log('[PDF] Removed page: v4p-pdf-page3 (Checklist)');
      }
      
      // On Page 1 (Order Details): hide vet practice section, adapt grid
      const page1 = document.getElementById('v4p-pdf-page1');
      if (page1) {
        // Find the 3-col grid and convert to 2-col (Pharmacy + Processed By)
        const threeColGrid = page1.querySelector('.v4p-pdf__info-grid--3col');
        if (threeColGrid) {
          const sections = threeColGrid.querySelectorAll('.v4p-pdf__section');
          // sections[0] = Pharmacy, sections[1] = Vet Practice, sections[2] = Responsible Staff
          if (sections[1]) sections[1].style.display = 'none';
          if (sections[2]) {
            const titleEl = sections[2].querySelector('.v4p-pdf__section-title');
            if (titleEl) titleEl.textContent = 'PROCESSED BY';
            const verifiedEl = sections[2].querySelector('.v4p-pdf__verified');
            if (verifiedEl) verifiedEl.style.display = 'none';
          }
          threeColGrid.classList.remove('v4p-pdf__info-grid--3col');
          threeColGrid.classList.add('v4p-pdf__info-grid--2col');
        }
      }

      // Page 4 (Customer Receipt) — hide vet + pharmacist, convert to 2-col
      const page4 = document.getElementById('v4p-pdf-page4');
      if (page4) {
        const threeColGrid = page4.querySelector('.v4p-pdf__info-grid--3col');
        if (threeColGrid) {
          const sections = threeColGrid.querySelectorAll('.v4p-pdf__section');
          if (sections[1]) sections[1].style.display = 'none';
          if (sections[2]) {
            const titleEl = sections[2].querySelector('.v4p-pdf__section-title');
            if (titleEl) titleEl.textContent = 'PROCESSED BY';
            const verifiedEl = sections[2].querySelector('.v4p-pdf__verified');
            if (verifiedEl) verifiedEl.style.display = 'none';
          }
          threeColGrid.classList.remove('v4p-pdf__info-grid--3col');
          threeColGrid.classList.add('v4p-pdf__info-grid--2col');
        }
      }

      // Receipt orders page too
      const page4Orders = document.getElementById('v4p-pdf-page4-orders');
      if (page4Orders) {
        const threeColGrid = page4Orders.querySelector('.v4p-pdf__info-grid--3col');
        if (threeColGrid) {
          const sections = threeColGrid.querySelectorAll('.v4p-pdf__section');
          if (sections[1]) sections[1].style.display = 'none';
          if (sections[2]) {
            const titleEl = sections[2].querySelector('.v4p-pdf__section-title');
            if (titleEl) titleEl.textContent = 'PROCESSED BY';
            const verifiedEl = sections[2].querySelector('.v4p-pdf__verified');
            if (verifiedEl) verifiedEl.style.display = 'none';
          }
          threeColGrid.classList.remove('v4p-pdf__info-grid--3col');
          threeColGrid.classList.add('v4p-pdf__info-grid--2col');
        }
      }
    }

    const pdfContainer = document.getElementById('v4p-pdf-container');
    const content = document.getElementById('v4p-pdf-content');

    const originalParent = pdfContainer.parentNode;
    const originalNextSibling = pdfContainer.nextSibling;

    
    if (!content) {
      throw new Error('PDF content not found');
    }
    
    // Move to <body> to avoid transformed ancestors breaking fixed positioning
    document.body.appendChild(pdfContainer);

    // Show container
    pdfContainer.style.display = 'block';
    pdfContainer.style.position = 'fixed';
    pdfContainer.style.left = '0';
    pdfContainer.style.top = '0';
    pdfContainer.style.width = '100vw';
    pdfContainer.style.visibility = 'hidden';
    pdfContainer.style.pointerEvents = 'none';

    // Wait for images
    const images = pdfContainer.querySelectorAll('img');
    await Promise.all(Array.from(images).map(img => {
      return new Promise(resolve => {
        if (img.complete && img.naturalHeight !== 0) resolve();
        else {
          img.onload = resolve;
          img.onerror = resolve;
          setTimeout(resolve, 3000);
        }
      });
    }));
    
    await new Promise(r => setTimeout(r, 300));
    
    const filename = generatePdfFilename(data);
    
    // Options for html2pdf
    const opt = {
      margin: 0,
      filename: filename,
      image: { type: 'jpeg', quality: 0.95 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        scrollX: 0,
        scrollY: 0,
        x: 0,
        y: 0,
        width: 794,
        windowWidth: 794,
        onclone: (doc) => {
          doc.documentElement.style.margin = '0';
          doc.documentElement.style.padding = '0';
          doc.body.style.margin = '0';
          doc.body.style.padding = '0';

          const c = doc.getElementById('v4p-pdf-content');
          if (c) {
            c.style.margin = '0';
            c.style.padding = '0';
            c.style.transform = 'none';
            c.style.left = '0';
          }
        }
      },
      jsPDF: { 
        unit: 'px', 
        format: [794, 1123], 
        orientation: 'portrait',
        hotfixes: ['px_scaling']
      },
      pagebreak: { 
        mode: ['css', 'legacy'], 
        before: '.v4p-pdf__page--orders, .v4p-pdf__page--continuation, .v4p-pdf__page--rx, .v4p-pdf__page--checklist, .v4p-pdf__page--receipt-cont'
      }
    };
    
    try {
      // Generate entire PDF at once - html2pdf will handle page breaks
      const pdfBlob = await html2pdf()
        .set(opt)
        .from(content)
        .outputPdf('blob');

      pdfContainer.style.visibility = 'visible';
      pdfContainer.style.display = 'none';

      // Restore DOM position
      if (originalNextSibling) originalParent.insertBefore(pdfContainer, originalNextSibling);
      else originalParent.appendChild(pdfContainer);

      
      return { blob: pdfBlob, filename: filename };
      
    } catch (error) {
      pdfContainer.style.visibility = 'visible';
      pdfContainer.style.display = 'none';

      // Restore DOM position
      if (originalNextSibling) originalParent.insertBefore(pdfContainer, originalNextSibling);
      else originalParent.appendChild(pdfContainer);

      throw error;
    }
  }

  // ============================================
  // UPLOAD
  // ============================================
  
  async function uploadPdfToWorker(pdfBlob, filename, orderData) {
    const base64 = await blobToBase64(pdfBlob);
    
    const payload = {
      pdf: base64,
      filename: filename,
      orderReference: orderData.order.reference,
      pharmacyId: orderData.pharmacy.accountId,
      metadata: {
        customerName: `${orderData.customer.firstName} ${orderData.customer.lastName}`,
        petName: orderData.pet.name,
        route: orderData.order.route,
        fulfilmentType: orderData.order.fulfilmentType,
        lineCount: orderData.totals.lineCount,
        total: orderData.totals.grandTotal,
        hasRepeats: orderData.order.hasRepeats,
        createdAt: orderData.order.timestamp,
        verificationComplete: orderData.verificationChecklist?.summary?.allRequiredComplete || false,
        signOffConfirmed: orderData.signOff?.confirmed || false
      }
    };
    
    const response = await fetch(`${CONFIG.pdfWorkerUrl}/order/store-pdf`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `Upload failed: HTTP ${response.status}`);
    }
    
    return response.json();
  }

  // ============================================
  // MAIN
  // ============================================
  
  async function generateAndUploadPdf(orderData) {
    console.log('Starting PDF generation...');
    
    const { blob: pdfBlob, filename } = await generatePdf(orderData);
    // DO NOT auto-download here (we'll only download if user clicks later)
    console.log('PDF generated:', pdfBlob.size, 'bytes, filename:', filename);

    
    let uploadResult = null;
    try {
      if (CONFIG.pdfWorkerUrl) {
        uploadResult = await uploadPdfToWorker(pdfBlob, filename, orderData);
        console.log('PDF uploaded:', uploadResult);
      }
    } catch (uploadError) {
      console.warn('PDF upload failed:', uploadError);
      uploadResult = { error: uploadError.message };
    }
    
    return {
      success: true,
      orderReference: orderData.order.reference,
      filename: filename,
      tempPdfId: uploadResult?.tempPdfId || uploadResult?.pdfId || null,
      pdfUrl: uploadResult?.pdfUrl || uploadResult?.previewUrl || null,
      pdfBlob: pdfBlob,
      uploaded: !uploadResult?.error,
      uploadError: uploadResult?.error || null
    };
  }

  async function previewPdfLocally(orderData) {
    const { blob: pdfBlob, filename } = await generatePdf(orderData);

    if (CONFIG.autoDownloadPdf) {
      downloadBlob(pdfBlob, filename);
    }


    const url = URL.createObjectURL(pdfBlob);
    return { pdfUrl: url, pdfBlob, filename, orderReference: orderData.order.reference };
  }

  // ============================================
  // HELPERS
  // ============================================
  
  function setText(id, value) {
    const el = document.getElementById(id);
    if (el) el.textContent = value || '';
  }
  
  function setIcon(id, isYes) {
    const el = document.getElementById(id);
    if (el) {
      el.textContent = isYes ? '✓' : '✗';
      el.style.background = isYes ? '#d4edda' : '#f8d7da';
      el.style.color = isYes ? '#155724' : '#721c24';
    }
  }
  
  function formatDate(dateStr) {
    if (!dateStr) return 'N/A';
    try { return new Date(dateStr).toLocaleDateString('en-GB'); }
    catch (e) { return dateStr; }
  }
  
  function formatDateTime(dateStr) {
    if (!dateStr) return 'N/A';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-GB') + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } catch (e) { return dateStr; }
  }
  
  function truncate(str, maxLen) {
    if (!str) return '';
    return str.length > maxLen ? str.substring(0, maxLen - 1) + '…' : str;
  }
  
  function escapeHtml(str) {
    if (!str) return '';
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  
  function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  // ============================================
  // EXPORT
  // ============================================
  
  window.V4PPdfGenerator = {
    populatePdfTemplate,
    generatePdf,
    generatePdfFilename,
    uploadPdfToWorker,
    generateAndUploadPdf,
    previewPdfLocally
  };

})();
